import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, GoldButton } from "@/components/admin/AdminLayout";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Wifi, Send, MessageSquare, RefreshCw, QrCode, LogOut, ChevronDown, ChevronUp, Copy, Phone } from "lucide-react";

export default function WhatsAppSettings() {
  const [cfg, setCfg] = useState(null);
  const [status, setStatus] = useState(null); // gateway health {ready, has_qr}
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testPhone, setTestPhone] = useState("");
  const [inboundLogs, setInboundLogs] = useState([]);
  const [qrData, setQrData] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [qrTimer, setQrTimer] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const load = async () => {
    const r = await api.get("/admin/whatsapp/config");
    setCfg({
      ...r.data,
      admin_numbers_str: (r.data.admin_numbers || []).join(", "),
    });
    // Auto-detect if advanced should be open (railway_url is required and missing)
    if (!r.data.railway_url) setShowAdvanced(true);
    // Fetch gateway health
    if (r.data.railway_url) {
      api.post("/admin/whatsapp/test-connection")
        .then(res => setStatus(res.data))
        .catch(() => setStatus({ ok: false }));
    }
    // Logs
    const logs = await api.get("/admin/whatsapp/inbound-logs?limit=20").catch(() => ({ data: { items: [] } }));
    setInboundLogs(logs.data.items || []);
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { return () => { if (qrTimer) clearInterval(qrTimer); }; }, [qrTimer]);

  if (!cfg) return <div className="p-10 text-slate-500">A carregar…</div>;

  const save = async (extra = {}) => {
    setSaving(true);
    try {
      const payload = {
        railway_url: cfg.railway_url,
        admin_numbers: cfg.admin_numbers_str.split(",").map(s => s.trim()).filter(Boolean),
        stale_timeout_min: Number(cfg.stale_timeout_min) || 60,
        ...extra,
      };
      if (cfg.inbound_secret && cfg.inbound_secret.trim()) payload.inbound_secret = cfg.inbound_secret.trim();
      await api.put("/admin/whatsapp/config", payload);
      toast.success("Guardado");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const testSend = async () => {
    if (!testPhone.trim()) return toast.error("Insira um número");
    if (!cfg.railway_url) return toast.error("Primeiro configure o gateway em Definições avançadas");
    setTesting(true);
    try {
      const r = await api.post("/admin/whatsapp/test-send", { to: testPhone.trim() });
      if (r.data.ok) toast.success("Mensagem de teste enviada!");
      else {
        const err = r.data.response?.error || r.data.error || "erro desconhecido";
        if (err === "client_not_ready") toast.error("WhatsApp ainda não autenticado. Faça o QR scan primeiro.");
        else toast.error("Falhou: " + err);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setTesting(false); }
  };

  const fetchQr = async () => {
    setQrLoading(true);
    try {
      const r = await api.get("/admin/whatsapp/qr");
      setQrData(r.data);
      if (r.data.ready) { setStatus({ ok: true, message: "Autenticado" }); load(); }
    } catch (e) {
      setQrData({ ok: false, error: formatApiError(e) });
    } finally { setQrLoading(false); }
  };
  const openQrModal = () => {
    setQrOpen(true);
    fetchQr();
    if (qrTimer) clearInterval(qrTimer);
    const id = setInterval(fetchQr, 5000);
    setQrTimer(id);
  };
  const closeQrModal = () => {
    setQrOpen(false);
    if (qrTimer) { clearInterval(qrTimer); setQrTimer(null); }
  };

  const changeNumber = async () => {
    if (!window.confirm("Desligar o WhatsApp atual e fazer scan com outro número?")) return;
    try {
      await api.post("/admin/whatsapp/logout");
      toast.success("A abrir QR do novo número…");
      setTimeout(openQrModal, 3000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const copyToClipboard = (text) => {
    const ok = () => toast.success("Copiado!");
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(ok, () => fallbackCopy(text, ok));
    } else fallbackCopy(text, ok);
  };
  const fallbackCopy = (text, onOk) => {
    try {
      const ta = document.createElement("textarea");
      ta.value = text; ta.style.position = "fixed"; ta.style.left = "-9999px";
      document.body.appendChild(ta); ta.select();
      const ok = document.execCommand("copy"); document.body.removeChild(ta);
      if (ok) onOk();
    } catch (e) { /* noop */ }
  };

  const inboundWebhookUrl = `${process.env.REACT_APP_BACKEND_URL || ""}/api/webhooks/whatsapp/inbound`;
  const gatewayReady = status?.gateway?.ready;
  const gatewayHasQr = status?.gateway?.has_qr;
  const gatewayOnline = status?.ok;
  const authenticatedNumber = status?.gateway?.wid;

  return (
    <div className="p-10 max-w-[900px]">
      <PageHeader
        eyebrow="Simplificado"
        title="WhatsApp"
        description="Autentique o WhatsApp Business e defina quem pode confirmar reservas por SMS/WhatsApp."
      />

      {/* STATUS CARD — Grande e prominente */}
      <div className={`border-2 p-8 mb-6 ${gatewayReady ? "border-green-200 bg-green-50" : gatewayHasQr ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-white"}`}>
        <div className="flex items-center gap-6">
          <div className={`w-20 h-20 rounded-full flex items-center justify-center flex-shrink-0 ${gatewayReady ? "bg-green-500" : gatewayHasQr ? "bg-amber-500" : "bg-slate-300"}`}>
            {gatewayReady ? <CheckCircle2 className="w-10 h-10 text-white" /> :
             gatewayHasQr ? <QrCode className="w-10 h-10 text-white" /> :
             <XCircle className="w-10 h-10 text-white" />}
          </div>
          <div className="flex-1">
            <p className="label-caps mb-1 text-slate-600">Estado do WhatsApp</p>
            {gatewayReady ? (
              <>
                <h3 className="text-2xl font-display text-green-800">Autenticado ✓</h3>
                <p className="text-sm text-green-700 mt-1 flex items-center gap-2">
                  <Phone className="w-4 h-4" /> +{authenticatedNumber || "número desconhecido"}
                </p>
              </>
            ) : gatewayHasQr ? (
              <>
                <h3 className="text-2xl font-display text-amber-800">Aguarda QR scan</h3>
                <p className="text-sm text-amber-700 mt-1">Clique em &quot;Ver QR Code&quot; e faça scan com o WhatsApp Business.</p>
              </>
            ) : gatewayOnline === false ? (
              <>
                <h3 className="text-2xl font-display text-red-800">Gateway offline</h3>
                <p className="text-sm text-red-700 mt-1">Configure primeiro o URL do Railway em &quot;Definições avançadas&quot;.</p>
              </>
            ) : (
              <>
                <h3 className="text-2xl font-display text-slate-700">Por configurar</h3>
                <p className="text-sm text-slate-500 mt-1">Configure primeiro o URL do Railway em &quot;Definições avançadas&quot;.</p>
              </>
            )}
          </div>
          <div className="flex flex-col gap-2 min-w-[180px]">
            {gatewayReady ? (
              <button
                onClick={changeNumber}
                className="px-4 py-3 border-2 border-red-300 bg-white hover:bg-red-50 text-red-700 font-semibold text-sm flex items-center justify-center gap-2"
                data-testid="wa-change-number-btn"
              >
                <LogOut className="w-4 h-4" /> Trocar número
              </button>
            ) : (
              <GoldButton
                onClick={openQrModal}
                disabled={!cfg.railway_url}
                data-testid="wa-show-qr"
              >
                <QrCode className="w-4 h-4 inline mr-2" /> Ver QR Code
              </GoldButton>
            )}
            <button
              onClick={load}
              className="text-xs px-3 py-1.5 border border-slate-300 hover:border-amber-500 flex items-center gap-1.5 justify-center"
              data-testid="wa-refresh"
            >
              <RefreshCw className="w-3 h-3" /> Actualizar estado
            </button>
          </div>
        </div>
      </div>

      {/* Admin numbers */}
      <div className="border border-slate-200 bg-white p-8 mb-6">
        <div className="flex items-start gap-6">
          <div className="flex-1">
            <p className="label-caps mb-2">Números Admin</p>
            <p className="text-sm text-slate-600 mb-4">
              Estes números recebem alertas e podem responder <code className="bg-slate-100 px-1 rounded">SIM DT-XXX</code> ou <code className="bg-slate-100 px-1 rounded">NAO DT-XXX</code> por WhatsApp para confirmar/cancelar reservas.
            </p>
            <Input
              value={cfg.admin_numbers_str}
              onChange={(e) => setCfg({ ...cfg, admin_numbers_str: e.target.value })}
              placeholder="+351911234567, +351919876543"
              data-testid="wa-admin-numbers"
              className="input-dt h-11 font-mono text-sm"
            />
            <p className="text-[11px] text-slate-500 mt-2">Separar múltiplos números com vírgula. Sempre com prefixo <strong>+351</strong>.</p>
          </div>
          <GoldButton onClick={() => save()} disabled={saving} data-testid="wa-save-admins">
            {saving ? "..." : "Guardar"}
          </GoldButton>
        </div>
      </div>

      {/* Test send */}
      {gatewayReady && (
        <div className="border border-slate-200 bg-white p-8 mb-6">
          <p className="label-caps mb-4"><Send className="w-3 h-3 inline mr-1" /> Enviar mensagem de teste</p>
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <Input
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="+351911234567"
                data-testid="wa-test-phone"
                className="input-dt h-11"
              />
            </div>
            <GoldButton onClick={testSend} disabled={testing} data-testid="wa-test-send">
              {testing ? "A enviar…" : "Enviar teste"}
            </GoldButton>
          </div>
        </div>
      )}

      {/* Inbound logs — só mostra se houver algum */}
      {inboundLogs.length > 0 && (
        <div className="border border-slate-200 bg-white p-8 mb-6">
          <div className="flex justify-between items-center mb-4">
            <p className="label-caps"><MessageSquare className="w-3 h-3 inline mr-1" /> Últimas mensagens recebidas</p>
            <button onClick={load} className="text-xs px-2 py-1 border border-slate-300 hover:border-amber-500 flex items-center gap-1">
              <RefreshCw className="w-3 h-3" />
            </button>
          </div>
          <div className="border border-slate-200 divide-y divide-slate-100 max-h-64 overflow-y-auto" data-testid="wa-inbound-log">
            {inboundLogs.map((log, i) => (
              <div key={i} className="p-3 text-sm flex gap-3">
                <div className="text-slate-400 text-xs font-mono w-32 flex-shrink-0">
                  {log.received_at ? new Date(log.received_at).toLocaleString("pt-PT") : "—"}
                </div>
                <div className="font-mono text-xs text-slate-600 w-36 flex-shrink-0">{log.recipient}</div>
                <div className="flex-1 text-slate-800 font-mono text-xs break-all">{log.body}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Advanced — collapsible */}
      <div className="border border-slate-200 bg-slate-50">
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="w-full p-4 flex items-center justify-between text-sm font-semibold text-slate-700 hover:bg-slate-100"
          data-testid="wa-advanced-toggle"
        >
          <span>⚙️ Definições avançadas do Gateway</span>
          {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        {showAdvanced && (
          <div className="p-6 border-t border-slate-200 bg-white space-y-5">
            {/* Copy to Railway */}
            <div className="border border-amber-200 bg-amber-50 p-4">
              <p className="label-caps mb-2 text-amber-900 text-xs">📋 Copiar para o Railway (Variables)</p>
              <div className="text-xs font-mono bg-white p-3 border border-amber-200 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <span><strong>DITOURS_INBOUND_URL</strong> = <span className="text-blue-700">{inboundWebhookUrl}</span></span>
                  <button onClick={() => copyToClipboard(inboundWebhookUrl)} className="text-slate-500 hover:text-amber-600 flex-shrink-0" data-testid="copy-inbound-url">
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
                <div>
                  <span><strong>DITOURS_INBOUND_SECRET</strong> = <span className="text-blue-700">{cfg.inbound_secret_preview || "(vazio — gere abaixo)"}</span></span>
                </div>
              </div>
            </div>

            <div>
              <label className="label-caps block mb-2">
                URL público do Railway
                <span className="text-slate-400 normal-case font-normal ml-2">(https://xxx.up.railway.app)</span>
              </label>
              <Input
                value={cfg.railway_url || ""}
                onChange={(e) => setCfg({ ...cfg, railway_url: e.target.value })}
                placeholder="https://ditours-whatsapp-production.up.railway.app"
                data-testid="wa-railway-url"
                className="input-dt h-11"
              />
            </div>

            <div>
              <label className="label-caps block mb-2">
                Segredo Inbound
                <span className="text-slate-400 normal-case font-normal ml-2">Actual: {cfg.inbound_secret_preview || "(vazio)"}</span>
              </label>
              <div className="flex gap-2">
                <Input
                  value={cfg.inbound_secret || ""}
                  onChange={(e) => setCfg({ ...cfg, inbound_secret: e.target.value })}
                  placeholder={cfg.inbound_secret_preview ? "(mudar? cole novo aqui)" : "cole ou gere"}
                  data-testid="wa-inbound-secret"
                  className="input-dt h-11 font-mono text-xs"
                />
                <button
                  type="button"
                  onClick={() => {
                    const arr = new Uint8Array(32);
                    crypto.getRandomValues(arr);
                    const hex = Array.from(arr, b => b.toString(16).padStart(2, '0')).join('');
                    setCfg({ ...cfg, inbound_secret: hex });
                  }}
                  className="px-3 py-2 border border-slate-300 hover:border-amber-500 text-xs whitespace-nowrap"
                  data-testid="wa-gen-secret"
                >
                  Gerar
                </button>
              </div>
            </div>

            <div className="flex gap-3">
              <GoldButton onClick={() => save()} disabled={saving} data-testid="wa-save-advanced">
                {saving ? "..." : "Guardar definições"}
              </GoldButton>
            </div>
          </div>
        )}
      </div>

      {/* QR Modal */}
      {qrOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={closeQrModal} data-testid="wa-qr-modal">
          <div className="bg-white rounded-lg p-8 max-w-md w-full text-center relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={closeQrModal} className="absolute top-3 right-3 w-8 h-8 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-100" data-testid="wa-qr-close">✕</button>
            <h3 className="font-display text-2xl mb-2">Autenticar WhatsApp</h3>
            <p className="text-sm text-slate-600 mb-6">
              No telemóvel: WhatsApp → Definições → <strong>Aparelhos ligados</strong> → <strong>Ligar um aparelho</strong>
            </p>

            {qrLoading && !qrData && (
              <div className="flex flex-col items-center gap-3 py-8">
                <RefreshCw className="w-8 h-8 animate-spin text-amber-500" />
                <p className="text-sm text-slate-500">A obter QR code…</p>
              </div>
            )}

            {qrData?.ok && qrData?.qr && (
              <>
                <div className="inline-block p-4 bg-white border-2 border-amber-400" data-testid="wa-qr-image">
                  <img src={qrData.qr} alt="WhatsApp QR" className="w-64 h-64" />
                </div>
                <p className="text-xs text-slate-500 mt-4">QR actualiza automaticamente. Feche este modal depois de ler.</p>
              </>
            )}

            {qrData && !qrData.ok && qrData.ready && (
              <div className="py-8">
                <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-3" />
                <p className="text-green-700 font-semibold">WhatsApp autenticado!</p>
                <button onClick={closeQrModal} className="mt-4 px-4 py-2 bg-green-600 text-white text-sm rounded">Fechar</button>
              </div>
            )}

            {qrData && !qrData.ok && !qrData.ready && qrData.error === "no_qr_yet" && (
              <div className="py-8">
                <RefreshCw className="w-8 h-8 animate-spin text-amber-500 mx-auto mb-3" />
                <p className="text-sm text-slate-600">A aguardar geração do QR (10-20s no primeiro arranque)…</p>
              </div>
            )}

            {qrData && !qrData.ok && qrData.error && !["no_qr_yet"].includes(qrData.error) && (
              <div className="py-8">
                <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
                <p className="text-sm text-red-700">Erro: {qrData.error}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
